import { Box, Checkbox, IconButton, Text, Tooltip } from '@chakra-ui/react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { FiPlus } from 'react-icons/fi';
import { ScheduleInterval } from './components/schedule-interval-input.component';

interface IProps {
	isLoading?: boolean;
	name: string;
	value: Array<{ start: string; end: string }>;
	onChange: (value: Array<{ start: string; end: string }>) => void;
	onRepeatClick: () => void;
}

export default function DayScheduleInput(props: IProps) {
	const { t } = useTranslation();

	function handleAddClick() {
		const newValue = _computeNewValue(props.value);
		props.onChange([...props.value, newValue]);
	}

	function handleIntervalChange(id: number, value: { start: string; end: string }) {
		const startMinutes = parseInt(value.start.split(':')[0], 10) * 60 + parseInt(value.start.split(':')[1], 10);
		const endMinutes = parseInt(value.end.split(':')[0], 10) * 60 + parseInt(value.end.split(':')[1], 10);

		if (endMinutes <= startMinutes) {
			return;
		}

		const newValue = [...props.value];
		newValue[id] = value;
		props.onChange(newValue);
	}

	function handleDeleteClick(index: number) {
		const newValue = [...props.value];
		newValue.splice(index, 1);
		props.onChange(newValue);
	}

	function handleCheckboxChange(e: React.ChangeEvent<HTMLInputElement>) {
		e.target.checked ? props.onChange([{ start: '09:00', end: '17:00' }]) : props.onChange([]);
	}

	return (
		<Box display="grid" gridTemplateRows="1fr" gridTemplateColumns="auto 1fr auto" alignItems="center" gap={2}>
			{/* FIXME: A veces value es null en produccion no se por que */}
			<Checkbox isChecked={props.value?.length > 0} checked={props.value?.length > 0} onChange={handleCheckboxChange} />

			<Box>
				<Text>{props.name}</Text>
			</Box>

			<Box>
				{!props.value?.length ? (
					<Tooltip ml="auto" label={t('Add interval')} placement="left">
						<IconButton
							aria-label={t('Add interval')}
							isDisabled={props.isLoading}
							variant="ghost"
							size="sm"
							icon={<FiPlus />}
							onClick={handleAddClick}
						/>
					</Tooltip>
				) : (
					<Box>
						{props.value
							.sort((a, b) => a.start.localeCompare(b.start))
							.map((interval, index) => {
								return (
									<ScheduleInterval
										shouldDisplayAdd={true}
										shouldDisplayRepeat={true}
										isLoading={props.isLoading}
										key={index}
										onAddClick={handleAddClick}
										onChange={(value) => handleIntervalChange(index, value)}
										onDeleteClick={() => handleDeleteClick(index)}
										onRepeatClick={props.onRepeatClick}
										value={interval}
									/>
								);
							})}
					</Box>
				)}
			</Box>
		</Box>
	);
}

function _computeNewValue(schedule: Array<{ start: string; end: string }>) {
	// Asumiendo que el array viene ordenado por horarios de inicio
	// Si no hay horarios, devolvemos el horario por defecto
	if (!schedule || schedule.length === 0) {
		return { start: '09:00', end: '17:00' };
	}

	const lastSchedule = schedule[schedule.length - 1];
	const [lastEndHour, lastEndMinute] = lastSchedule.end.split(':').map(Number);

	// Calcular el nuevo inicio como una hora después del último fin, si es antes de las 23:00
	const newStartHour = lastEndHour + 1;
	let newStartMinute = lastEndMinute;

	// Verificar si el nuevo inicio es válido
	if (newStartHour < 23) {
		let newEndHour = newStartHour + 1; // Asumiendo una duración de 8 horas para el nuevo horario
		if (newEndHour >= 24) {
			newEndHour = 23; // Ajustar para no sobrepasar las 23:59
			newStartMinute = 59;
		}
		return {
			start: `${newStartHour.toString().padStart(2, '0')}:${newStartMinute.toString().padStart(2, '0')}`,
			end: `${newEndHour.toString().padStart(2, '0')}:${newStartMinute.toString().padStart(2, '0')}`,
		};
	}

	return { start: '09:00', end: '17:00' };
}
